import type { ThreeElements } from '@react-three/fiber'
import { useDetalhesFinos, useNivelQualidade } from '../ambiente/contextoQualidade'
import { COMPRIMENTO_MOLA_REPOUSO, malhasPuncao } from './malhasPuncao'

interface Props extends Omit<ThreeElements['group'], 'children'> {
  progresso?: number
  ativa?: boolean
}

const LARANJA = '#ea580c'
const LARANJA_ATIVO = '#f97316'
const PRETO_PONTA = '#18181b'
const ACO_INOX = '#cbd5e1'
const PRATA = '#f1f5f9'

/**
 * Punção de vidro com mola, inspirado no Holmatro.
 * Guarda a silhueta e as cores (laranja e preto), sem logo nem texto.
 *
 * - Ponta torneada: carbeto encostado no vidro em x ≈ 0,166, corpo preto de
 *   apoio dos dedos com sulco anatômico e colar que recebe a mola;
 * - mola helicoidal de inox, um arame contínuo, que estica com a tração;
 * - empunhadura laranja levemente cônica, com anéis de pega e clipe de bolso.
 *
 * A animação é a mesma de antes: até 72% do progresso o socorrista puxa a
 * empunhadura 5,5 cm para trás; depois a mola dispara de volta.
 */
export function PuncaoHolmatro3D({ progresso = 0, ativa = false, ...props }: Props) {
  const nivel = useNivelQualidade()
  const detalhes = useDetalhesFinos()
  const malhas = malhasPuncao(nivel)

  const p = Math.max(0, Math.min(1, progresso))
  const tracao = p < 0.72
    ? (p / 0.72) * 0.055
    : Math.max(0, (1 - (p - 0.72) / 0.28) * 0.055)

  const xInicioMola = 0.11
  const xFimMola = -0.015 - tracao
  const comprimentoMola = xInicioMola - xFimMola

  return (
    <group {...props}>
      {/* 1. Ponta fixa no vidro */}
      <mesh geometry={malhas.ponta} castShadow>
        <meshStandardMaterial color={PRETO_PONTA} metalness={0.7} roughness={0.38} />
      </mesh>

      {/* 2. Haste guia e mola, que acompanham a tração */}
      <mesh position={[(xInicioMola + xFimMola) / 2, 0, 0]} rotation={[0, 0, Math.PI / 2]}>
        <cylinderGeometry args={[0.004, 0.004, comprimentoMola, 12]} />
        <meshStandardMaterial color={ACO_INOX} metalness={0.92} roughness={0.2} />
      </mesh>
      <mesh geometry={malhas.mola} position={[xFimMola, 0, 0]} scale={[comprimentoMola / COMPRIMENTO_MOLA_REPOUSO, 1, 1]}>
        <meshStandardMaterial color={ACO_INOX} metalness={0.94} roughness={0.16} />
      </mesh>

      {/* 3. Empunhadura de tração, que recua com a mola */}
      <group position={[-tracao, 0, 0]}>
        <mesh geometry={malhas.empunhadura} castShadow>
          <meshStandardMaterial color={ativa ? LARANJA_ATIVO : LARANJA} metalness={0.3} roughness={0.38} />
        </mesh>

        {/* Clipe de bolso com parafuso de fixação */}
        {detalhes && (
          <group position={[-0.065, 0.0105, 0]}>
            <mesh>
              <boxGeometry args={[0.055, 0.0018, 0.007]} />
              <meshStandardMaterial color={PRATA} metalness={0.92} roughness={0.16} />
            </mesh>
            <mesh position={[0.028, -0.001, 0]} rotation={[0, 0, 0.35]}>
              <boxGeometry args={[0.008, 0.0018, 0.007]} />
              <meshStandardMaterial color={PRATA} metalness={0.92} roughness={0.16} />
            </mesh>
            <mesh position={[-0.022, 0.0018, 0]}>
              <cylinderGeometry args={[0.003, 0.003, 0.0035, 6]} />
              <meshStandardMaterial color={PRATA} metalness={0.95} roughness={0.12} />
            </mesh>
          </group>
        )}
      </group>
    </group>
  )
}
